import React, { useEffect, useState } from "react";
import moment from "moment";
import { ThreeDots } from "react-loader-spinner";
import { FiBriefcase, FiCalendar, FiDollarSign, FiFileText, FiUser } from "react-icons/fi";
import { getInvoiceApi } from "../../../../Api/ClientWork/ClientWork";
import ActionButton from "../../../Common/ActionButton";
import CommonDetailModal from "../../../Common/CommonDetailModal";
import "../../../Accounts/Contracts/Contract/ContractDetailModal.css";
import "./InvoiceDetailModal.css";

const formatAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? `PKR ${amount.toLocaleString("en-PK")}` : "N/A";
};

const formatDate = (value) => (value ? moment(value).format("DD-MMM-YYYY") : "N/A");

function InvoiceDetailModal({ isOpen, onClose, invoiceData, invoiceId }) {
  const [invoice, setInvoice] = useState(invoiceData || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const id = invoiceId || invoiceData?.Id;
    if (!id) {
      setInvoice(invoiceData || null);
      return;
    }

    let isMounted = true;
    (async () => {
      if (!invoiceData) {
        setLoading(true);
      }
      try {
        const response = await getInvoiceApi(id);
        if (isMounted && response?.data?.IsSuccess && response.data.Data) {
          setInvoice(response.data.Data);
        } else if (isMounted && invoiceData) {
          setInvoice(invoiceData);
        }
      } catch (error) {
        if (isMounted && invoiceData) {
          setInvoice(invoiceData);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [invoiceId, invoiceData]);

  if (!isOpen) {
    return null;
  }

  const DetailField = ({ label, value, span = 1, noLeftBorder = false }) => (
    <div
      className={`invoice-detail-field col-span-${span} ${noLeftBorder ? "no-left-border" : ""}`}
    >
      <div className="contract-detail-field__label">{label}</div>
      <div className="invoice-detail-value">
        {value !== null && value !== undefined && value !== "" ? value : "N/A"}
      </div>
    </div>
  );

  const DetailSectionHeader = ({ title, Icon, rightContent }) => (
    <div
      className="contract-detail-card__header d-flex justify-content-between align-items-center"
      style={{ minHeight: "44px", padding: "8px 14px" }}
    >
      <div className="d-flex align-items-center" style={{ gap: "9px" }}>
        <span className="contract-detail-card__icon">{Icon && <Icon />}</span>
        <b className="m-0" style={{ fontSize: "14px" }}>
          {title}
        </b>
      </div>
      {rightContent}
    </div>
  );

  const status = invoice?.Status || "Draft";
  const statusColors = {
    Paid: { background: "#d4edda", color: "#155724", border: "#c3e6cb" },
    Cancelled: { background: "#f8d7da", color: "#842029", border: "#f5c2c7" },
    Authorized: { background: "#dbeafe", color: "#1e40af", border: "#bfdbfe" },
    "Partially Received": { background: "#fff3cd", color: "#856404", border: "#ffeeba" },
    Draft: { background: "#e2e8f0", color: "#475569", border: "#cbd5e1" },
  };
  const statusStyle = statusColors[status] || statusColors.Draft;

  return (
    <CommonDetailModal
      isOpen={isOpen}
      onClose={onClose}
      title="Invoice Detail"
      maxWidth="1040px"
      contentClassName="modal-content-screen1 contract-detail-modal invoice-detail-modal"
      topContent={
        <div className="invoice-detail-content">
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "180px" }}
            >
              <ThreeDots color="#5cd1d0" height={40} width={40} />
            </div>
          ) : (
            <>
              <div className="row mt-2">
                <div className="col-lg-6 col-12 mb-3 invoice-detail-column">
                  <div className="contract-detail-card h-100">
                    <DetailSectionHeader
                      title="Invoice & Identity"
                      Icon={FiFileText}
                      rightContent={
                        <span
                          className="invoice-status-badge"
                          style={{ ...statusStyle, border: `1px solid ${statusStyle.border}` }}
                        >
                          {status}
                        </span>
                      }
                    />
                    <div className="invoice-detail-metrics">
                      <DetailField
                        label="Bill No"
                        value={invoice?.BillNumber}
                        span={1}
                        noLeftBorder
                      />

                      <DetailField
                        label="Contract No"
                        value={invoice?.ContractNo}
                        span={1}
                        noLeftBorder
                      />
                      <DetailField label="Billing Date" value={formatDate(invoice?.BillingDate)} />
                    </div>
                  </div>
                </div>

                <div className="col-lg-6 col-12 mb-3 invoice-detail-column">
                  <div className="contract-detail-card h-100">
                    <DetailSectionHeader title="Client & Project" Icon={FiUser} />
                    <div className="invoice-detail-metrics">
                      <DetailField
                        label="Project Owner"
                        value={invoice?.ClientName}
                        span={2}
                        noLeftBorder
                      />
                      <DetailField label="NTN Number" value={invoice?.ClientNTN} />
                      <DetailField
                        label="Project Name"
                        value={invoice?.ProjectName}
                        span={2}
                        noLeftBorder
                      />
                      <DetailField
                        label="Contract Value"
                        value={formatAmount(invoice?.OriginalContractValue)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12 mb-3 invoice-detail-column">
                  <div className="contract-detail-card">
                    <DetailSectionHeader
                      title="Billing Period"
                      Icon={FiCalendar}
                      rightContent={
                        <span className="text-muted" style={{ fontSize: "12px", fontWeight: 600 }}>
                          {formatDate(invoice?.PeriodFrom)} - {formatDate(invoice?.PeriodTo)}
                        </span>
                      }
                    />
                    <div className="invoice-detail-metrics-4col">
                      <DetailField
                        label="Period From"
                        value={formatDate(invoice?.PeriodFrom)}
                        noLeftBorder
                      />
                      <DetailField label="Period To" value={formatDate(invoice?.PeriodTo)} />
                      <DetailField
                        label="Cumulative Gross"
                        value={formatAmount(invoice?.CumulativeGross)}
                      />
                      <DetailField
                        label="Previous Gross"
                        value={formatAmount(invoice?.PreviousGross)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12 mb-3 invoice-detail-column">
                  <div className="contract-detail-card">
                    <DetailSectionHeader title="Amounts & Deductions" Icon={FiDollarSign} />
                    <div className="invoice-detail-metrics-4col">
                      <DetailField
                        label="Current Gross"
                        value={formatAmount(invoice?.CurrentGross)}
                        noLeftBorder
                      />
                      <DetailField label="Retention" value={`${invoice?.RetentionRate || 0}%`} />
                      <DetailField
                        label="Retention Amount"
                        value={formatAmount(invoice?.RetentionAmount)}
                      />
                      <DetailField
                        label="Advance Amortization"
                        value={formatAmount(invoice?.AdvanceAmortization)}
                      />
                      <DetailField
                        label="Income Tax"
                        value={`${invoice?.IncomeTaxRate || 0}%`}
                        noLeftBorder
                      />
                      <DetailField
                        label="Income Tax Amount"
                        value={formatAmount(invoice?.IncomeTaxAmount)}
                      />
                      <DetailField label="Sales Tax" value={`${invoice?.SalesTaxRate || 0}%`} />
                      <DetailField
                        label="Sales Tax Amount"
                        value={formatAmount(invoice?.SalesTaxAmount)}
                      />
                      <DetailField
                        label="Sales Tax Charged"
                        value={`${invoice?.SalesTaxChargedRate || 0}%`}
                        noLeftBorder
                      />
                      <DetailField
                        label="Sales Tax Charged Amount"
                        value={formatAmount(invoice?.SalesTaxChargedAmount)}
                      />
                      <DetailField
                        label="Other Adjustments"
                        value={formatAmount(invoice?.OtherAdjustments)}
                      />
                      <DetailField
                        label="Adjustment Notes"
                        value={invoice?.AdjustmentNotes || "None"}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12 mb-3 invoice-detail-column">
                  <div className="contract-detail-card">
                    <DetailSectionHeader title="Payment Summary" Icon={FiBriefcase} />
                    <div className="invoice-detail-summary">
                      <div className="invoice-detail-summary-item">
                        <div className="invoice-detail-summary-label">Total Deductions</div>
                        <div className="invoice-detail-summary-value">
                          {formatAmount(invoice?.TotalDeductions)}
                        </div>
                      </div>
                      <div className="invoice-detail-summary-item invoice-detail-summary-item--net">
                        <div className="invoice-detail-summary-label">Net Receivable</div>
                        <div className="invoice-detail-summary-value">
                          {formatAmount(invoice?.NetReceivable)}
                        </div>
                      </div>
                      <div className="invoice-detail-summary-item">
                        <div className="invoice-detail-summary-label">Received Amount</div>
                        <div className="invoice-detail-summary-value">
                          {formatAmount(invoice?.ReceivedAmount)}
                        </div>
                      </div>
                      <div className="invoice-detail-summary-item">
                        <div className="invoice-detail-summary-label">Outstanding</div>
                        <div className="invoice-detail-summary-value">
                          {formatAmount(invoice?.Outstanding)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {invoice?.Status === "Cancelled" && invoice?.CancelledReason && (
                <div className="row">
                  <div className="col-12 mb-3 invoice-detail-column">
                    <div className="contract-detail-card">
                      <DetailSectionHeader title="Cancellation Details" Icon={FiFileText} />
                      <div className="p-3">
                        <div className="contract-detail-field__label">Cancelled Reason</div>
                        <div className="invoice-detail-value">{invoice.CancelledReason}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="row mt-2">
                <div
                  className="col-12 d-flex flex-wrap justify-content-end"
                  style={{ gap: "10px" }}
                >
                  <ActionButton text="Close" variant="grey" onClick={onClose} />
                </div>
              </div>
            </>
          )}
        </div>
      }
    />
  );
}

export default InvoiceDetailModal;
